import { ApiService } from "@/api/services/ApiService";
import {
  IBooking,
  IBookingCalendar,
  IBookingStatusReason,
  ICheckinRequest,
  ICheckoutRequest,
  IProblemDetails,
  IWorkOrder,
  IWorkOrderContact,
  IWorkOrderNotes,
  IWorkOrderOtherTech,
  IWorkOrderOwnerIncident,
  IWorkOrderPosts,
} from "@/lib/types";

// ---------------------- Service

export class BookingsService {
  // --------- Variables

  private static API_URL = import.meta.env.VITE_API_URL;

  // --------- Methods

  // ----- GET

  static async getBookingsCurrent(bookableResourceId: string): Promise<IBooking[]> {
    const params = new URLSearchParams({ bookableResourceId });

    return await ApiService.sendRequest(`${this.API_URL}/api/bookings/current?${params}`, {
      method: "GET",
    });
  }

  static async getBookingsCalendar(bookableResourceId: string): Promise<IBookingCalendar[]> {
    const params = new URLSearchParams({ bookableResourceId });

    return await ApiService.sendRequest(`${this.API_URL}/api/bookings/calendar?${params}`, {
      method: "GET",
    });
  }

  static async getStatusReasons(): Promise<IBookingStatusReason[]> {
    return await ApiService.sendRequest(`${this.API_URL}/api/bookings/statuses`, {
      method: "GET",
    });
  }

  static async getWorkOrder(bookingId: string): Promise<IWorkOrder> {
    const params = new URLSearchParams({ bookingId });

    return await ApiService.sendRequest(`${this.API_URL}/api/workorder?${params}`, {
      method: "GET",
    });
  }

  static async getWorkOrderNotes(id: string): Promise<IWorkOrderNotes[]> {
    const params = new URLSearchParams({ id });

    return await ApiService.sendRequest(
      `${this.API_URL}/api/workorder/notes?type=workorder&${params}`,
      {
        method: "GET",
      },
    );
  }

  static async getWorkOrderPosts(id: string): Promise<IWorkOrderPosts[]> {
    const params = new URLSearchParams({ id });

    return await ApiService.sendRequest(
      `${this.API_URL}/api/workorder/posts?type=workorder&${params}`,
      {
        method: "GET",
      },
    );
  }

  static async getWorkOrderContact(contactId: string): Promise<IWorkOrderContact> {
    const params = new URLSearchParams({ contactId });

    return await ApiService.sendRequest(`${this.API_URL}/api/workorder/contact?${params}`, {
      method: "GET",
    });
  }

  static async getWorkOrderOwnerByIncident(id: string): Promise<IWorkOrderOwnerIncident> {
    const params = new URLSearchParams({ id });

    return await ApiService.sendRequest(
      `${this.API_URL}/api/workorder/owner?type=incident&${params}`,
      {
        method: "GET",
      },
    );
  }

  static async getWorkOrderOtherTechs(
    workOrderName: string,
    resourceId: string,
    date: string,
  ): Promise<IWorkOrderOtherTech[]> {
    const params = new URLSearchParams({ workOrderName, resourceId, date });

    return await ApiService.sendRequest(`${this.API_URL}/api/workorder/techs?${params}`, {
      method: "GET",
    });
  }

  // ----- POST

  static async checkIn(data: ICheckinRequest): Promise<void | IProblemDetails> {
    return await ApiService.sendRequest(`${this.API_URL}/api/bookings/checkin`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
  }

  static async checkOut(data: ICheckoutRequest): Promise<void | IProblemDetails> {
    return await ApiService.sendRequest(`${this.API_URL}/api/bookings/checkout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
  }
}
